<template>
  <div v-if="categories.length" class="search-results" :class="{ '-mobile': isMobile }">
    <div class="list">
      <div v-for="category in categories" :key="category.key" class="item">
        <div class="category-name">
          {{ getCategoryLabel(category.key) }}
        </div>

        <div class="category-items">
          <button
            v-for="resultItem in category.items"
            :key="`${category.key}-${resultItem.id}`"
            class="search-item"
            type="button"
            @click="emit('select', resultItem.link)"
          >
            <span class="item-icon">
              <BaseIconSvoi :icon="getCategoryIcon(category.key)" size="full" />
            </span>

            <p class="text">
              <template
                v-for="(part, partIndex) in splitMatchParts(
                  getItemDisplayName(category.key, resultItem),
                  query
                )"
                :key="`${category.key}-${resultItem.id}-${partIndex}`"
              >
                <strong v-if="part.highlighted">{{ part.value }}</strong>
                <span v-else>{{ part.value }}</span>
              </template>
            </p>
          </button>
        </div>
      </div>
    </div>
  </div>

  <p v-else-if="showNoResults" class="empty-state">По запросу ничего не найдено</p>
</template>

<script lang="ts" setup>
  import { BaseIconSvoi } from '~/components/base-ui/icon'
  import {
    getCategoryIcon,
    getCategoryLabel,
    getItemDisplayName,
    splitMatchParts,
  } from '~/composables/global-search/search-items'
  import type { GlobalSearchItems } from '~/shared/interfaces/search'

  const props = withDefaults(
    defineProps<{
      items: GlobalSearchItems
      query: string
      showNoResults: boolean
      isMobile?: boolean
    }>(),
    {
      isMobile: false,
    }
  )

  const emit = defineEmits<{
    (e: 'select', link: string): void
  }>()

  const categories = computed(() =>
    Object.entries(props.items)
      .filter(([, categoryItems]) => categoryItems.length > 0)
      .map(([key, categoryItems]) => ({ key, items: categoryItems }))
  )
</script>

<style lang="scss" scoped>
  .search-results {
    display: grid;
    justify-content: center;
    width: 100%;

    > .list {
      display: grid;
      gap: 8px;
    }

    > .list > .item {
      display: grid;
      grid-template-columns: 120px minmax(0, 670px);
      gap: 16px;
      max-width: 1200px;

      @include vp-1279 {
        grid-template-columns: 120px 1fr;
        max-width: calc(100vw - 80px);
      }
    }

    > .list > .item > .category-name {
      @extend %font-14;

      padding: 16px 0;
      color: $color-potato-30;
    }

    > .list > .item > .category-items {
      display: grid;
      gap: 8px;
    }

    &.-mobile {
      justify-content: stretch;

      > .list > .item {
        grid-template-columns: 1fr;
        gap: 8px;
        max-width: 100%;
      }

      > .list > .item > .category-name {
        padding: 0;
      }

      .search-item {
        padding: 14px 16px;
        background: $main-back-color;
      }
    }
  }

  .search-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 16px 30px;
    border: none;
    border-radius: 18px;
    background: $color-default-white;
    text-align: left;
    transition: $trans-default;

    @include hover-focus {
      cursor: pointer;
      background: $main-back-color;
    }

    > .item-icon {
      display: flex;
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      color: $color-potato-30;
    }

    > .text {
      @extend %font-16;

      color: $color-default-black;

      > strong {
        color: $color-kiwi-50;
        font-weight: 600;
      }
    }

    @include vp-1439 {
      padding: 16px;
    }
  }

  .empty-state {
    @extend %font-16;

    display: flex;
    justify-content: center;
    width: 100%;
    padding: 16px 0;
    color: $color-potato-60;
  }
</style>
